import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/API";
import { useAuth } from "../hooks/useAuth";

const WORKSPACE_STORAGE_KEY = "selectedWorkspaceId";
const WorkspaceContext = createContext(null);

const normalizeWorkspace = (workspace) => ({
  id: String(
    workspace?.id ??
      workspace?.id_workspace ??
      workspace?.workspace_id ??
      ""
  ),
  nome:
    workspace?.nome ??
    workspace?.name ??
    workspace?.descricao ??
    "Workspace sem nome",
  descricao: workspace?.descricao ?? "",
  papel: workspace?.papel ?? "member",
  ativo: workspace?.ativo ?? true,
});

const parseWorkspaceResponse = (response) => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Nao foi possivel carregar os workspaces.");
  }

  return response?.data?.records || response?.data?.data || [];
};

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem("user");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    console.error("WorkspaceProvider: erro ao ler usuario salvo", error);
    return null;
  }
};

const getUserId = (user) => user?.id ?? user?.userId ?? user?.id_usuario ?? null;

const getUserDisplayName = (user) =>
  user?.nome ??
  user?.name ??
  user?.descricao ??
  user?.email?.split("@")?.[0] ??
  "Usuario";

export const WorkspaceProvider = ({ children }) => {
  const { isAuthenticated, user, loading: loadingAuth } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceIdState] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const autoCreatingWorkspaceRef = useRef(false);

  const setSelectedWorkspaceId = useCallback((workspaceId) => {
    const normalizedId = String(workspaceId || "");
    setSelectedWorkspaceIdState(normalizedId);

    if (normalizedId) {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, normalizedId);
    } else {
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/workspaces");
      const records = parseWorkspaceResponse(response)
        .map(normalizeWorkspace)
        .filter((workspace) => workspace.id);

      setWorkspaces(records);

      const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY) || "";
      const hasStoredWorkspace = records.some((workspace) => workspace.id === storedWorkspaceId);

      if (hasStoredWorkspace) {
        setSelectedWorkspaceIdState(storedWorkspaceId);
      } else if (records.length > 0) {
        const fallbackId = records[0].id;
        setSelectedWorkspaceIdState(fallbackId);
        localStorage.setItem(WORKSPACE_STORAGE_KEY, fallbackId);
      } else {
        setSelectedWorkspaceIdState("");
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      }
    } catch (loadError) {
      console.error("WorkspaceProvider: erro ao carregar workspaces", loadError);
      setError(
        loadError?.response?.data?.message ||
          loadError?.message ||
          "Nao foi possivel carregar os workspaces."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const createWorkspace = useCallback(
    async ({ nome, descricao, idUsuarioCriador }) => {
      const normalizedName = nome.trim();

      try {
        const response = await api.post("/workspaces", {
          nome: normalizedName,
          descricao,
          id_usuario_criador: idUsuarioCriador,
        });

        if (response?.data?.status === false) {
          throw new Error(response?.data?.message || "Nao foi possivel criar o workspace.");
        }

        const createdRecord = response?.data?.record;
        await refreshWorkspaces();

        const createdId = String(
          createdRecord?.id ??
            createdRecord?.id_workspace ??
            createdRecord?.workspace_id ??
            ""
        );

        if (createdId) {
          setSelectedWorkspaceId(createdId);
        }

        return {
          record: createdRecord || null,
          recoveredAfterTimeout: false,
        };
      } catch (error) {
        const isTimeout =
          error?.response?.status === 504 ||
          error?.code === "ECONNABORTED" ||
          /timeout/i.test(error?.message || "");

        if (!isTimeout) {
          throw error;
        }

        for (let attempt = 0; attempt < 4; attempt += 1) {
          await wait(1200);

          try {
            const response = await api.get("/workspaces");
            const records = parseWorkspaceResponse(response)
              .map(normalizeWorkspace)
              .filter((workspace) => workspace.id);

            setWorkspaces(records);

            const recoveredWorkspace =
              records.find(
                (workspace) => workspace.nome.trim().toLowerCase() === normalizedName.toLowerCase()
              ) || null;

            if (recoveredWorkspace) {
              setSelectedWorkspaceId(recoveredWorkspace.id);
              return {
                record: recoveredWorkspace,
                recoveredAfterTimeout: true,
              };
            }
          } catch (refreshError) {
            console.error("WorkspaceProvider: erro ao validar criacao apos timeout", refreshError);
          }
        }

        throw new Error(
          "O servidor demorou demais para responder e nao conseguimos confirmar se o workspace foi criado. Atualize a lista e verifique se ele apareceu."
        );
      }
    },
    [refreshWorkspaces, setSelectedWorkspaceId]
  );

  const ensureDefaultWorkspace = useCallback(async () => {
    const user = getStoredUser();
    const userId = getUserId(user);

    if (!userId || autoCreatingWorkspaceRef.current) {
      return false;
    }

    autoCreatingWorkspaceRef.current = true;

    try {
      await createWorkspace({
        nome: `Workspace de ${getUserDisplayName(user)}`,
        descricao: "Workspace inicial criado automaticamente ao entrar na aplicacao.",
        idUsuarioCriador: userId,
      });
      return true;
    } catch (creationError) {
      console.error("WorkspaceProvider: erro ao criar workspace padrao", creationError);
      setError(
        creationError?.response?.data?.message ||
          creationError?.message ||
          "Nao foi possivel criar o workspace inicial automaticamente."
      );
      return false;
    } finally {
      autoCreatingWorkspaceRef.current = false;
    }
  }, [createWorkspace]);

  useEffect(() => {
    if (loadingAuth) {
      return;
    }

    if (!isAuthenticated) {
      setWorkspaces([]);
      setSelectedWorkspaceIdState("");
      setError("");
      setLoading(false);
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      return;
    }

    refreshWorkspaces();
  }, [isAuthenticated, loadingAuth, refreshWorkspaces]);

  useEffect(() => {
    if (loadingAuth || !isAuthenticated || loading || workspaces.length > 0) {
      return;
    }

    ensureDefaultWorkspace();
  }, [ensureDefaultWorkspace, isAuthenticated, loading, loadingAuth, user, workspaces]);

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === selectedWorkspaceId) || null,
    [workspaces, selectedWorkspaceId]
  );

  const value = useMemo(
    () => ({
      workspaces,
      selectedWorkspaceId,
      selectedWorkspace,
      loading,
      error,
      setSelectedWorkspaceId,
      createWorkspace,
      refreshWorkspaces,
    }),
    [
      workspaces,
      selectedWorkspaceId,
      selectedWorkspace,
      loading,
      error,
      setSelectedWorkspaceId,
      createWorkspace,
      refreshWorkspaces,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error("useWorkspace deve ser usado dentro de WorkspaceProvider.");
  }

  return context;
};
